import { cmsShipmentRoutes } from "./cms-routes"
import type { ShipmentStats } from "./schema"
export type { ShipmentStats } from "./schema"

// Exact API labels only. Category queries include every subtype, including "".
export const shipmentTaxonomy = {
  all: ["", undefined, ""],
  pets: ["Pets and animals", undefined, "pet"],
  dogs: ["Pets and animals", "Dogs", "dog"],
  cats: ["Pets and animals", "Cats", "cat"],
  horses: ["Pets and animals", "Horses", "horse"],
  livestock: ["Pets and animals", "Cattle and Livestock", "livestock"],
  boats: ["Boats", undefined, "boat"],
  powerboats: ["Boats", "Power Boats", "powerboat"],
  sailboats: ["Boats", "Sail Boats", "sailboat"],
  yachts: ["Boats", "Yachts", "yacht"],
  jetSkis: ["Boats", "Jet Ski", "jet ski"],
  motorcycles: ["Motorcycles", undefined, "motorcycle"],
  atvs: ["Motorcycles", "ATV", "ATV"],
  vehicles: ["Vehicles", undefined, "vehicle"],
  cars: ["Vehicles", "Cars and SUVs", "car and SUV"],
  rvs: ["Vehicles", "RV", "RV"],
  trucks: ["Vehicles", "Truck", "truck"],
  household: ["Household Goods", undefined, "household goods"],
  furniture: ["Household Goods", "Furniture", "furniture"],
  equipment: ["Heavy Equipment", undefined, "heavy equipment"],
  tractors: ["Heavy Equipment", "Tractor", "tractor"],
  freight: ["Freight", undefined, "freight"],
  moves: ["Apartment or Household Moves", undefined, "household move"],
  specialCare: [
    "Items with Special Care Requirements",
    undefined,
    "special-care",
  ],
} as const
export type ShipmentConcept = keyof typeof shipmentTaxonomy

export const shipmentRoutes: Record<string, ShipmentConcept> = {
  ...cmsShipmentRoutes,
  "/listing/new/dogs": "dogs",
  "/drivers": "all",
  "/package-delivery": "all",
  "/shipping-large-items": "all",
}
const aliases: Partial<Record<ShipmentConcept, string[]>> = {
  all: [
    "",
    "index",
    "home-page",
    "about-us",
    "how-it-works",
    "new-drivers",
    "cost-to-ship",
  ],
  pets: [
    "pet-transportation",
    "pet-shipping",
    "pet-transport",
    "breeders",
    "partners",
    "animal-transporter-guide",
  ],
  dogs: [
    "dog-transportation",
    "dog-shipping",
    "puppy-transportation",
    "dog-transportation-msf",
  ],
  cats: ["cat-transportation", "cat-shipping"],
  horses: ["horse-transportation"],
  livestock: ["livestock-transportation"],
  boats: ["boat-transportation", "boat-shipping"],
  powerboats: ["powerboat-transportation", "powerboat-shipping"],
  sailboats: ["sailboat-transportation", "sailboat-shipping"],
  yachts: ["yacht-transportation", "yacht-shipping"],
  jetSkis: ["jet-ski-shipping", "jet-ski-transport"],
  motorcycles: [
    "motorcycle-transportation",
    "motorcycle-shipping",
    "motorcycle-carriers",
  ],
  atvs: ["atv-shipping"],
  vehicles: ["vehicle-transportation"],
  cars: ["car-shipping", "car-transportation"],
  rvs: ["rv-transport"],
  trucks: ["truck-transport"],
  household: ["household-goods-shipping"],
  furniture: ["shipping-furniture", "household-shipping"],
  equipment: ["equipment-shipping", "heavy-equipment-shipping"],
  tractors: ["tractor-transport"],
  freight: ["freight-shipping-quotes"],
  moves: ["apartment-moves"],
  specialCare: ["specialcare"],
}

for (const [concept, paths] of Object.entries(aliases)) {
  for (const path of paths) {
    shipmentRoutes[`/${path}`] = concept as ShipmentConcept
  }
}
export function normalizeShipmentPath(path: string): string {
  return `/${path.split(/[?#]/)[0].replace(/^\/+|\/+$/g, "")}`
}

// Pages outside the shipment-stat rollout whose experience cards still follow
// the founding year.
export function hasExperienceYearsOnly(path: string): boolean {
  return normalizeShipmentPath(path) === "/pallet-shipping"
}
export function getShipmentConcept(path: string): ShipmentConcept | null {
  const normalized = normalizeShipmentPath(path)

  if (
    normalized === "/offers" ||
    normalized.startsWith("/offers/") ||
    normalized === "/cat-transportation-a-b-test"
  ) {
    return null
  }

  if (shipmentRoutes[normalized]) {
    return shipmentRoutes[normalized]
  }

  if (/^\/drivers\/\d+$/.test(normalized)) {
    return "all"
  }

  // These are known programmatic templates, not fuzzy category matching.
  for (const prefix of [
    "pet-shipping",
    "dog-shipping",
    "boat-transportation",
    "vehicle-transportation",
    "cost-to-ship",
  ]) {
    if (normalized.startsWith(`/${prefix}/`)) {
      return shipmentRoutes[`/${prefix}`]
    }
  }

  return null
}
export function getTotalCompletedShipments(data: ShipmentStats): number {
  return data.totals.matches
}
export function getCategoryCompletedShipments(
  data: ShipmentStats,
  type: string,
): number | null {
  const rows = data.rows.filter((row) => row.shipmentType === type)

  return rows.length ? rows.reduce((sum, row) => sum + row.matches, 0) : null
}
export function getSubtypeCompletedShipments(
  data: ShipmentStats,
  type: string,
  subtype: string,
): number | null {
  return (
    data.rows.find(
      (row) => row.shipmentType === type && row.shipmentSubtype === subtype,
    )?.matches ?? null
  )
}
export function formatCompletedShipments(value: number | null): string | null {
  if (value === null || !Number.isSafeInteger(value) || value <= 0) {
    return null
  }

  const step = [1000, 100].find((size) => value >= size) ?? 1

  return `${(Math.floor(value / step) * step).toLocaleString("en-US")}${
    step > 1 ? "+" : ""
  }`
}
export interface CompletedShipmentValue {
  raw: number | null
  formatted: string
  plain: string
  label: string
  text: string
  covered: ShipmentStats["covered"]
  coveredFrom: string
  coveredTo: string
}
// Listing totals exist only marketplace-wide; deliveries also break down by type.
function selectShipmentCount(
  data: ShipmentStats,
  concept: ShipmentConcept,
  metric: "matches" | "listings",
): number | null {
  if (metric === "listings") {
    return concept === "all" ? data.totals.listings ?? null : null
  }

  if (concept === "all") {
    return getTotalCompletedShipments(data)
  }

  const [type, subtype] = shipmentTaxonomy[concept]

  return subtype === undefined
    ? getCategoryCompletedShipments(data, type)
    : getSubtypeCompletedShipments(data, type, subtype)
}
export function completedShipmentStat(
  data: ShipmentStats | null,
  concept: ShipmentConcept,
  metric: "matches" | "listings" = "matches",
): CompletedShipmentValue | null {
  if (!data) {
    return null
  }

  const label = shipmentTaxonomy[concept][2]
  const raw = selectShipmentCount(data, concept, metric)
  const formatted = formatCompletedShipments(raw)

  if (!formatted) {
    return null
  }

  const prefix = label ? `${label} ` : ""
  let text = `${formatted} ${prefix}deliveries since 2008`

  if (metric === "listings") {
    text = `${formatted} shipments listed on CitizenShipper since 2008.`
  } else if (concept === "all") {
    text = `${formatted} deliveries across CitizenShipper since 2008`
  }

  return {
    raw,
    formatted,
    plain: formatted.replace(/\+$/, ""),
    label: `${prefix}shipments completed`,
    text,
    covered: data.covered,
    coveredFrom: new Date(`${data.covered.from}T00:00:00Z`).toLocaleDateString(
      "en-US",
      {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC",
      },
    ),
    coveredTo: new Date(`${data.covered.to}T00:00:00Z`).toLocaleDateString(
      "en-US",
      {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC",
      },
    ),
  }
}

// Approved wording for these landing pages; variants retain their own copy.
export const landingFooterLabels: Record<string, string> = {
  "/dog-transportation": "dogs safely transported",
  "/cat-transportation": "cats safely transported nationwide since 2008.",
  "/motorcycle-transportation":
    "motorcycle deliveries completed nationwide through CitizenShipper since 2008.",
  "/motorcycle-carriers": "motorcycle deliveries",
  "/car-shipping": "cars and SUVs delivered",
  "/shipping-furniture": "furniture deliveries",
  "/equipment-shipping": "heavy equipment deliveries",
  "/freight-shipping-quotes": "freight deliveries",
  "/apartment-moves": "household moves completed",
  "/specialcare": "special-care deliveries",
  "/military-moves": "pets safely transported",
  "/military-moves-discounts": "pets safely transported",
  "/veteran-moves-discounts": "pets safely transported",
}

export const landingHeaderProof: Record<
  string,
  { concept: ShipmentConcept; period?: "last12Months"; label: string }
> = {
  "/boat-transportation": {
    concept: "boats",
    label: "boats delivered nationwide through CitizenShipper.",
  },
  "/rv-transport": {
    concept: "rvs",
    label: "RV deliveries completed nationwide through CitizenShipper.",
  },
  "/package-delivery": {
    concept: "all",
    label: "shipments delivered nationwide through CitizenShipper.",
  },
  "/motorcycle-transportation": {
    concept: "motorcycles",
    label: "motorcycle deliveries completed nationwide through CitizenShipper.",
  },
  "/cat-transportation": {
    concept: "cats",
    period: "last12Months",
    label: "cats safely transported nationwide last year.",
  },
}
